import { Descriptions, Grid, Tabs } from 'antd';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import * as permissionKind from '../../../../../shared/permissions';
// eslint-disable-next-line max-len
import { type LocalCustomerManagementModuleWithPermissionsSpecsFragment } from '../../../../api/fragments/LocalCustomerManagementModuleWithPermissionsSpecs';
import ConsolePageWithHeader from '../../../../layouts/ConsoleLayout/ConsolePageWithHeader';
import hasPermissions from '../../../../utilities/hasPermissions';
import useFormattedSimpleVersioning from '../../../../utilities/useFormattedSimpleVersioning';
import LocalCustomerModuleActions from './LocalCustomerModuleAction';
import LocalCustomerModuleInner from './LocalCustomerModuleInner';
import { HeaderTabs } from './shared';

export type LocalCustomerModulePageProps = { module: LocalCustomerManagementModuleWithPermissionsSpecsFragment };

const LocalCustomerModulePage = ({ module }: LocalCustomerModulePageProps) => {
    const { t } = useTranslation(['localCustomerModuleDetails']);
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState(HeaderTabs.KycPresets);
    const { updated, offset } = useFormattedSimpleVersioning({
        versioning: module.versioning,
        timeZone: module.company.timeZone,
    });

    const screens = Grid.useBreakpoint();
    const permissions = {
        hasUpdatePermission: hasPermissions(module.permissions, [permissionKind.updateModule]),
        hasDeletePermission: hasPermissions(module.permissions, [permissionKind.deleteModule]),
    };

    const content = (
        <Descriptions column={{ xs: 1, md: 2 }} bordered>
            <Descriptions.Item label={t('localCustomerModuleDetails:content.displayName')}>
                {module.displayName}
            </Descriptions.Item>
            <Descriptions.Item label={t('localCustomerModuleDetails:content.company')}>
                {module.company.displayName}
            </Descriptions.Item>
            <Descriptions.Item {...t('common:fields.updated', { returnObjects: true, offset })}>
                {updated}
            </Descriptions.Item>
        </Descriptions>
    );

    return (
        <ConsolePageWithHeader
            content={content}
            extra={<LocalCustomerModuleActions module={module} permissions={permissions} />}
            header={{
                footer: (
                    <Tabs activeKey={activeTab} onChange={(activeKey: HeaderTabs) => setActiveTab(activeKey)}>
                        <Tabs.TabPane
                            key={HeaderTabs.KycPresets}
                            tab={t('localCustomerModuleDetails:tabs.kycPresets.title')}
                        />
                        <Tabs.TabPane
                            key={HeaderTabs.KycFields}
                            tab={t('localCustomerModuleDetails:tabs.kycFields.title')}
                        />
                    </Tabs>
                ),
            }}
            onBack={() => navigate('/admin/system/modules')}
            title={
                screens.md ? t('localCustomerModuleDetails:title', { name: module.displayName }) : module.displayName
            }
        >
            <LocalCustomerModuleInner activeTab={activeTab} module={module} permissions={permissions} />
        </ConsolePageWithHeader>
    );
};

export default LocalCustomerModulePage;
